<template>
  <div class="inline-flex">
    <input ref="fileInputRef" class="hidden" type="file" :multiple="multiple" :accept="acceptAttribute" @change="handleFileSelection">
    <button v-bind="attrs" class="inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60" type="button" :disabled="isDisabled" @click="openFilePicker">
      <slot :is-uploading="isUploading">
        {{ buttonLabel }}
      </slot>
    </button>
  </div>
</template>

<script setup>
import axios from 'axios'
import { computed, ref, useAttrs } from 'vue'
import { buildGeneratedUploadFilename } from '../utils/uploadFilename.js'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  uploadableId: {
    type: [String, Number],
    default: null,
  },
  uploadableType: {
    type: String,
    default: null,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  fileTypes: {
    type: [Array, String],
    default: null,
  },
  imagesOnly: {
    type: Boolean,
    default: false,
  },
  uploadUrl: {
    type: String,
    default: '/upload',
  },
  label: {
    type: String,
    default: 'Upload file',
  },
  uploadingLabel: {
    type: String,
    default: 'Uploading...',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['uploaded', 'error', 'upload-start', 'upload-finish'])

const attrs = useAttrs()
const fileInputRef = ref(null)
const isUploading = ref(false)

const normalizedAcceptedFileTypes = computed(() => {
  if (props.imagesOnly) {
    return ['image/*']
  }

  if (Array.isArray(props.fileTypes)) {
    return props.fileTypes
      .map((fileType) => normalizeFileType(fileType))
      .filter((fileType) => fileType !== '')
  }

  if (typeof props.fileTypes === 'string' && props.fileTypes.trim() !== '') {
    return props.fileTypes
      .split(',')
      .map((fileType) => normalizeFileType(fileType))
      .filter((fileType) => fileType !== '')
  }

  return []
})

const acceptAttribute = computed(() => {
  if (normalizedAcceptedFileTypes.value.length === 0) {
    return undefined
  }

  return normalizedAcceptedFileTypes.value.join(',')
})

const isDisabled = computed(() => {
  return props.disabled || isUploading.value
})

const buttonLabel = computed(() => {
  return isUploading.value ? props.uploadingLabel : props.label
})

const createUploadError = (message, files = []) => {
  return {
    message,
    files,
  }
}

const normalizeFileType = (fileType) => {
  if (typeof fileType !== 'string') {
    return ''
  }

  return fileType.trim().toLowerCase()
}

const isAllowedByToken = (file, token) => {
  const fileMimeType = (file.type ?? '').toLowerCase()
  const fileName = (file.name ?? '').toLowerCase()

  if (token.endsWith('/*')) {
    return fileMimeType.startsWith(token.slice(0, -1))
  }

  if (token.startsWith('.')) {
    return fileName.endsWith(token)
  }

  if (token.includes('/')) {
    return fileMimeType === token
  }

  return fileName.endsWith(`.${token}`)
}

const isFileAllowed = (file) => {
  if (normalizedAcceptedFileTypes.value.length === 0) {
    return true
  }

  return normalizedAcceptedFileTypes.value.some((token) => isAllowedByToken(file, token))
}

const openFilePicker = () => {
  if (isDisabled.value) {
    return
  }

  fileInputRef.value?.click()
}

const resolveBlobExtension = (mimeType) => {
  const normalizedMimeType = typeof mimeType === 'string' ? mimeType.trim().toLowerCase() : ''

  if (normalizedMimeType === '') {
    return 'bin'
  }

  const mimeTypeToExtension = {
    'image/jpeg': 'jpg',
    'image/svg+xml': 'svg',
    'text/plain': 'txt',
  }

  if (mimeTypeToExtension[normalizedMimeType]) {
    return mimeTypeToExtension[normalizedMimeType]
  }

  const [, subtype = 'bin'] = normalizedMimeType.split('/')

  return subtype.split('+')[0] || 'bin'
}

const normalizeUploadItem = (uploadItem, index) => {
  if (typeof Blob === 'undefined') {
    return null
  }

  if (!(uploadItem instanceof Blob)) {
    return null
  }

  if (typeof File !== 'undefined' && uploadItem instanceof File) {
    const fileName = uploadItem.name?.trim() ?? ''

    if (fileName !== '' && fileName.toLowerCase() !== 'blob') {
      return uploadItem
    }
  }

  const prefix = (uploadItem.type ?? '').toLowerCase().startsWith('image/') ? 'pasted-image' : 'upload'
  const extension = resolveBlobExtension(uploadItem.type)
  const filename = buildGeneratedUploadFilename({
    prefix,
    extension,
    suffix: String(index + 1),
  })

  if (typeof File === 'undefined') {
    return null
  }

  return new File([uploadItem], filename, {
    type: uploadItem.type || 'application/octet-stream',
    ...(typeof File !== 'undefined' && uploadItem instanceof File ? { lastModified: uploadItem.lastModified } : {}),
  })
}

const normalizeUploadItems = (uploadItems) => {
  let items = []

  if (typeof FileList !== 'undefined' && uploadItems instanceof FileList) {
    items = Array.from(uploadItems)
  } else if (Array.isArray(uploadItems)) {
    items = uploadItems
  } else if (uploadItems) {
    items = [uploadItems]
  }

  return items
    .map((item, index) => normalizeUploadItem(item, index))
    .filter((item) => item !== null)
}

const appendFilesToFormData = (formData, files) => {
  if (props.multiple) {
    files.forEach((file) => {
      formData.append('file[]', file)
    })

    return
  }

  formData.append('file', files[0])
}

const appendPolymorphicAttributesToFormData = (formData) => {
  if (props.uploadableId !== null && props.uploadableId !== '') {
    formData.append('uploadable_id', String(props.uploadableId))
  }

  if (props.uploadableType !== null && props.uploadableType !== '') {
    formData.append('uploadable_type', props.uploadableType)
  }
}

const resetFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const uploadFiles = async (uploadItems) => {
  if (isDisabled.value) {
    const error = createUploadError('Uploads are currently disabled.')
    emit('error', error)
    return null
  }

  const selectedFiles = normalizeUploadItems(uploadItems)

  if (selectedFiles.length === 0) {
    const error = createUploadError('No valid files were provided for upload.')
    emit('error', error)
    return null
  }

  if (!props.multiple && selectedFiles.length > 1) {
    const error = createUploadError('Only one file can be uploaded.', selectedFiles)
    emit('error', error)
    return null
  }

  const hasDisallowedFile = selectedFiles.some((file) => !isFileAllowed(file))
  if (hasDisallowedFile) {
    const error = createUploadError('One or more selected files are not allowed.', selectedFiles)
    emit('error', error)
    return null
  }

  const formData = new FormData()
  appendFilesToFormData(formData, selectedFiles)
  appendPolymorphicAttributesToFormData(formData)

  isUploading.value = true
  emit('upload-start')

  try {
    const response = await axios.post(props.uploadUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    emit('uploaded', response.data)
    return response.data
  } catch (error) {
    emit('error', error)
    throw error
  } finally {
    isUploading.value = false
    emit('upload-finish')
  }
}

const uploadBlob = async (blob) => {
  return uploadFiles(blob)
}

const handleFileSelection = async (event) => {
  const selectedFiles = Array.from(event.target?.files ?? [])

  resetFileInput()

  await uploadFiles(selectedFiles).catch(() => null)
}

defineExpose({
  openFilePicker,
  resetFileInput,
  uploadFiles,
  uploadBlob,
})
</script>
